import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Send } from "lucide-react";

import { HubContactAutocomplete } from "@/components/ui/HubContactAutocomplete";
import { HubContactTokenInput } from "@/components/ui/HubContactTokenInput";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { ensureHubContactSelection, hubContactSuggestionStub, type HubContactSelection } from "@/lib/hubContactsApi";
import { cn } from "@/lib/utils";
import { fetchMrpRfqEmailPreview, sendMrpRfqEmail, type MrpRfqEmailPreview } from "@/lib/rfqApi";

function ModalShell({
  children,
  maxWidth = "max-w-4xl",
  onBackdrop,
}: {
  children: React.ReactNode;
  maxWidth?: string;
  onBackdrop?: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onBackdrop?.();
        }
      }}
    >
      <div className={cn("max-h-[90vh] w-full overflow-hidden rounded-lg bg-white shadow-xl", maxWidth)}>
        {children}
      </div>
    </div>
  );
}

async function ensureContactsForEmails(emails: string[]): Promise<HubContactSelection[]> {
  const uniqueEmails = [
    ...new Set(emails.map((email) => email.trim().toLowerCase()).filter((email) => email.length > 0)),
  ];
  const contacts: HubContactSelection[] = [];
  for (const email of uniqueEmails) {
    const contact = await ensureHubContactSelection(
      hubContactSuggestionStub({ email, displayName: email }),
    );
    contacts.push(contact);
  }
  return contacts;
}

export interface RfqEmailModalProps {
  rfqId: number;
  onClose: () => void;
  onSent: () => void;
}

export function RfqEmailModal({ rfqId, onClose, onSent }: RfqEmailModalProps): JSX.Element | null {
  const [preview, setPreview] = useState<MrpRfqEmailPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [recipientContact, setRecipientContact] = useState<HubContactSelection | null>(null);
  const [ccContacts, setCcContacts] = useState<HubContactSelection[]>([]);
  const [searchAllContacts, setSearchAllContacts] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);
    void fetchMrpRfqEmailPreview(rfqId)
      .then((loaded) => {
        if (cancelled) return;
        setPreview(loaded);
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setPreviewError(error.message);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rfqId]);

  useEffect(() => {
    if (preview == null) {
      setRecipientContact(null);
      setCcContacts([]);
      return;
    }
    const primary = preview.recipients[0];
    const ccEmails = preview.recipients.slice(1).map((recipient) => recipient.email);
    let cancelled = false;
    void (async () => {
      if (primary != null) {
        const contact = await ensureHubContactSelection(
          hubContactSuggestionStub({
            email: primary.email,
            displayName: primary.name ?? primary.email,
          }),
        );
        if (!cancelled) {
          setRecipientContact(contact);
        }
      } else {
        setRecipientContact(null);
      }
      const cc = await ensureContactsForEmails(ccEmails);
      if (!cancelled) {
        setCcContacts(cc);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [preview?.rfqId, preview?.recipients]);

  useEffect(() => {
    setSearchAllContacts(false);
  }, [preview?.rfqId]);

  const sendMutation = useMutation({
    mutationFn: () => {
      if (recipientContact == null) {
        throw new Error("Select a recipient.");
      }
      return sendMrpRfqEmail(rfqId, {
        recipientEmail: recipientContact.email,
        ccEmails: ccContacts.map((contact) => contact.email),
      });
    },
    onSuccess: () => {
      onSent();
      onClose();
    },
  });

  const sendDisabled =
    loadingPreview || preview == null || recipientContact == null || sendMutation.isPending;
  const recipientSearchHint = preview?.recipients[0]?.email ?? "";
  const freshdeskCompanyId = preview?.supplierFreshdeskCompanyId ?? null;
  const allowUnscopedContactSearch = freshdeskCompanyId == null || searchAllContacts;

  return (
    <ModalShell maxWidth="max-w-4xl" onBackdrop={onClose}>
      <div className="flex max-h-[90vh] flex-col">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Email RFQ</h2>
          {preview != null ? (
            <p className="mt-1 text-sm text-slate-500">
              {preview.rfqNumber}
              {preview.supplierName ? ` · ${preview.supplierName}` : ""}
              {" · "}
              From {preview.fromMailbox}
            </p>
          ) : null}
        </div>

        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-6 py-4 lg:grid-cols-[320px_1fr]">
          <div className="space-y-4">
            <div>
              <Label htmlFor="rfq-email-recipient">Recipient</Label>
              {freshdeskCompanyId != null && preview?.supplierFreshdeskCompanyName ? (
                <label className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    checked={searchAllContacts}
                    disabled={loadingPreview || sendMutation.isPending}
                    onChange={(event) => setSearchAllContacts(event.target.checked)}
                  />
                  Search all contacts (not limited to {preview.supplierFreshdeskCompanyName})
                </label>
              ) : null}
              <HubContactAutocomplete
                id="rfq-email-recipient"
                contact={recipientContact}
                onContactChange={setRecipientContact}
                searchHint={recipientSearchHint}
                freshdeskCompanyId={freshdeskCompanyId}
                allowUnscopedSearch={allowUnscopedContactSearch}
                placeholder="Search contacts…"
                disabled={loadingPreview || sendMutation.isPending}
                className="mt-1"
              />
              {preview != null && preview.recipients.length > 0 ? (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  {preview.recipients.map((recipient) => (
                    <li key={recipient.email}>
                      {recipient.name != null && recipient.name.trim() !== ""
                        ? `${recipient.name} <${recipient.email}>`
                        : recipient.email}
                    </li>
                  ))}
                </ul>
              ) : preview != null ? (
                <p className="mt-2 text-xs text-amber-800">
                  No supplier email is on file. Search the contact directory or update the supplier record.
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="rfq-email-cc">Carbon copy</Label>
              <HubContactTokenInput
                id="rfq-email-cc"
                recipients={ccContacts}
                onRecipientsChange={setCcContacts}
                freshdeskCompanyId={freshdeskCompanyId}
                allowUnscopedSearch={allowUnscopedContactSearch}
                disabled={loadingPreview || sendMutation.isPending}
                placeholder="Search contacts…"
                className="mt-1"
              />
            </div>
            {preview != null ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</p>
                <p className="mt-1 text-sm text-slate-800">{preview.subject}</p>
              </div>
            ) : null}
            {preview != null ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Email attachments{" "}
                  <span className="font-normal normal-case tabular-nums">
                    (
                    {preview.externalAttachments.length === 1
                      ? "1 file"
                      : `${preview.externalAttachments.length} files`}
                    )
                  </span>
                </p>
                {preview.externalAttachments.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-600">No supplier documents attached.</p>
                ) : (
                  <ul className="mt-2 space-y-1 text-sm text-slate-800">
                    {preview.externalAttachments.map((attachment) => (
                      <li key={`${attachment.filename}-${attachment.line_number ?? "header"}`}>
                        {attachment.filename}
                        {attachment.line_number != null
                          ? ` (line ${attachment.line_number}${attachment.line_sku ? ` · ${attachment.line_sku}` : ""})`
                          : " (RFQ header)"}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
            {previewError != null ? (
              <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {previewError}
              </p>
            ) : null}
            {sendMutation.error != null ? (
              <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {(sendMutation.error as Error).message}
              </p>
            ) : null}
          </div>
          <div className="min-h-[520px] overflow-hidden rounded-md border border-slate-200 bg-white">
            {loadingPreview ? (
              <div className="flex h-full min-h-[520px] items-center justify-center text-sm text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Loading preview…
              </div>
            ) : preview != null ? (
              <iframe
                title={`RFQ email preview for ${preview.rfqNumber}`}
                srcDoc={preview.htmlBody}
                className="h-full min-h-[520px] w-full"
              />
            ) : (
              <div className="flex h-full min-h-[520px] items-center justify-center px-6 text-sm text-slate-500">
                Email preview is unavailable.
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={sendDisabled}
            onClick={() => sendMutation.mutate()}
          >
            {sendMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
            Send
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
